import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrderManagementData } from '../../context-api/order-management/OrderManagementDataContext';
import {
  DEFAULT_ORDER_TYPE_VIEW,
  NEXT_DAY_PROMO_DAY_TODAY,
  ORDER_TYPE_NEXT_DAY_PROMOTION,
} from './orderManagementConfig';
import { filterOrdersForMapView, ordersToMapMarkers } from './orderManagementMapUtils';
import {
  ACTIVE_ORDERS_DEFAULT_TIMEZONE,
  formatDateForActiveOrdersApi,
  getNextDayPromoDayKeys,
  getNextDayPromoDefaultDates,
  getRegionalBranchForOrdersApi,
  parseActiveOrdersResponse,
} from './orderManagementUtils';

const DEFAULT_MAP_FILTERS = {
  orderTypeView: DEFAULT_ORDER_TYPE_VIEW,
  deliveryDate: formatDateForActiveOrdersApi(),
  statusFilter: 'all',
  orderIdSearch: '',
  deliveryTypeFilter: 'all',
  nextDayPromoDayFilter: NEXT_DAY_PROMO_DAY_TODAY,
};

export function useOrderMapPageOrders() {
  const location = useLocation();
  const navState = location.state;

  const { fetchActiveOrders, fetchNextDayPromoStoreOrders } = useOrderManagementData();

  const [allOrders, setAllOrders] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const filters = useMemo(
    () => ({
      orderTypeView: navState?.orderTypeView ?? DEFAULT_MAP_FILTERS.orderTypeView,
      deliveryDate: navState?.deliveryDate ?? DEFAULT_MAP_FILTERS.deliveryDate,
      statusFilter: navState?.statusFilter ?? DEFAULT_MAP_FILTERS.statusFilter,
      orderIdSearch: navState?.orderIdSearch ?? DEFAULT_MAP_FILTERS.orderIdSearch,
      deliveryTypeFilter: navState?.deliveryTypeFilter ?? DEFAULT_MAP_FILTERS.deliveryTypeFilter,
      nextDayPromoDayFilter:
        navState?.nextDayPromoDayFilter ?? DEFAULT_MAP_FILTERS.nextDayPromoDayFilter,
    }),
    [
      navState?.orderTypeView,
      navState?.deliveryDate,
      navState?.statusFilter,
      navState?.orderIdSearch,
      navState?.deliveryTypeFilter,
      navState?.nextDayPromoDayFilter,
    ]
  );

  const isNextDayPromo = filters.orderTypeView === ORDER_TYPE_NEXT_DAY_PROMOTION;

  const nextDayPromoDayKeys = useMemo(
    () => getNextDayPromoDayKeys(),
    [refreshNonce] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      if (isNextDayPromo) {
        const res = await fetchNextDayPromoStoreOrders({
          date: getNextDayPromoDefaultDates(),
          regionalBranch: getRegionalBranchForOrdersApi(),
          page: 1,
          limit: 100,
          timeZone: ACTIVE_ORDERS_DEFAULT_TIMEZONE,
        });
        const parsed = parseActiveOrdersResponse(res);
        if (!parsed.ok) {
          setLoadError(parsed.message);
          setAllOrders([]);
          return;
        }
        setAllOrders(parsed.orders);
        setRefreshNonce((n) => n + 1);
        return;
      }

      const res = await fetchActiveOrders({
        date: filters.deliveryDate,
        regionalBranch: getRegionalBranchForOrdersApi(),
        page: 1,
        limit: 100,
        timeZone: ACTIVE_ORDERS_DEFAULT_TIMEZONE,
      });
      const parsed = parseActiveOrdersResponse(res);
      if (!parsed.ok) {
        setLoadError(parsed.message);
        setAllOrders([]);
        return;
      }
      setAllOrders(parsed.orders);
      setRefreshNonce((n) => n + 1);
    } finally {
      setLoading(false);
    }
  }, [fetchActiveOrders, fetchNextDayPromoStoreOrders, filters.deliveryDate, isNextDayPromo]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filteredOrders = useMemo(
    () =>
      filterOrdersForMapView(allOrders, {
        ...filters,
        nextDayPromoDayKeys,
      }),
    [allOrders, filters, nextDayPromoDayKeys]
  );

  const markers = useMemo(() => ordersToMapMarkers(filteredOrders), [filteredOrders]);

  const ordersWithoutLocation = useMemo(() => {
    const withCoords = new Set(markers.map((m) => m.id));
    return filteredOrders.filter((o) => !withCoords.has(o._id ?? o.order_no));
  }, [filteredOrders, markers]);

  return {
    loading,
    loadError,
    filteredOrders,
    markers,
    ordersWithoutLocation,
    filters,
    isNextDayPromo,
    regionalBranch: getRegionalBranchForOrdersApi(),
    refresh: loadOrders,
    orderTypeView: filters.orderTypeView,
  };
}

export function buildOrderMapNavigationState(ordersState) {
  return {
    orderTypeView: ordersState.orderTypeView,
    deliveryDate: ordersState.deliveryDate,
    statusFilter: ordersState.statusFilter,
    orderIdSearch: ordersState.orderIdSearch,
    deliveryTypeFilter: ordersState.deliveryTypeFilter,
    nextDayPromoDayFilter: ordersState.nextDayPromoDayFilter,
  };
}
